import { z } from 'zod';
import { MatchingStateSchema } from './enums.schema.js';
// Phase 21 (3.1-5) — recurring facility shift schedule. Weekly-by-day-of-week
// + a local time window over a date range (≤ 90 inclusive days). On create it
// eagerly materializes every occurrence into independent SCHEDULED shift
// requests (× headcount); the horizon cron broadcasts each as it matures.
const HHMM = z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "must be 'HH:mm' 24-hour time");
const DATE_ONLY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be 'YYYY-MM-DD'");
/** Max span of a schedule's date range, INCLUSIVE, in calendar days. Mirrors
 *  MAX_SCHEDULE_DAYS + the shift_schedule_range_valid_chk DB CHECK. */
const MAX_SCHEDULE_INCLUSIVE_DAYS = 90;
export const ShiftScheduleCreateRequestSchema = z
    .object({
    roleId: z.string().uuid(),
    // ISO weekdays: 1 = Mon … 7 = Sun. Non-empty.
    daysOfWeek: z.array(z.number().int().min(1).max(7)).min(1).max(7),
    startTimeLocal: HHMM,
    endTimeLocal: HHMM,
    rangeStart: DATE_ONLY,
    rangeEnd: DATE_ONLY,
    // Headcount per occurrence (3.1-1). Each occurrence posts this many requests.
    caregiversPerOccurrence: z.number().int().min(1).max(20).optional(),
    requiredSkills: z.array(z.string()).default([]),
    offerTierSize: z.number().int().min(1).max(20).optional(),
    offerExpirationMinutes: z.number().int().min(1).max(1440).optional(),
    // 3.1-8 — stamped onto the first slot of EACH occurrence (per-occurrence
    // independent priority offer + 24h fallback; Principle-9 safe).
    preferredCaregiverId: z.string().uuid().optional(),
    specialConstraints: z.string().max(2000).optional(),
})
    .superRefine((val, ctx) => {
    const start = Date.parse(`${val.rangeStart}T00:00:00Z`);
    const end = Date.parse(`${val.rangeEnd}T00:00:00Z`);
    if (Number.isFinite(start) && Number.isFinite(end)) {
        if (end < start) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'rangeEnd must not precede rangeStart',
                path: ['rangeEnd'],
            });
        }
        else if ((end - start) / 86_400_000 > MAX_SCHEDULE_INCLUSIVE_DAYS - 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `The schedule range can span at most ${MAX_SCHEDULE_INCLUSIVE_DAYS} days.`,
                path: ['rangeEnd'],
            });
        }
    }
});
export const ShiftScheduleResponseSchema = z.object({
    id: z.string().uuid(),
    roleId: z.string().uuid(),
    roleCode: z.string(),
    setting: z.string(),
    daysOfWeek: z.array(z.number().int()),
    startTimeLocal: z.string(),
    endTimeLocal: z.string(),
    timezone: z.string(),
    rangeStart: z.string(),
    rangeEnd: z.string(),
    caregiversPerOccurrence: z.number().int(),
    preferredCaregiverId: z.string().uuid().nullable(),
    status: z.enum(['ACTIVE', 'CANCELLED']),
    // How many shift-request rows this schedule materialized (× headcount).
    occurrenceCount: z.number().int(),
    createdAt: z.string(),
});
/**
 * List query — `includeInactive` defaults false so the schedules tab shows only
 * schedules that still matter: ACTIVE **and** not past their `rangeEnd`.
 *
 * Two buckets accumulate forever otherwise:
 *   · CANCELLED — the facility cancelled the sequence.
 *   · PAST — the range fully elapsed. NOTE there is no COMPLETED status and no
 *     cron that retires a finished schedule: `ShiftScheduleStatus` is only
 *     {ACTIVE, CANCELLED}, so an elapsed schedule stays ACTIVE forever. "Past"
 *     is DERIVED from `rangeEnd` rather than stored — no migration, no job to
 *     drift. A schedule that started in the past but runs into the future is
 *     still current (the check keys on rangeEnd only).
 *
 * The toggle reveals both on demand; the response always carries
 * `inactiveCount` so the UI can label it while they're hidden.
 */
export const ShiftScheduleListQuerySchema = z.object({
    // NB: NOT z.coerce.boolean() — that uses JS Boolean(), so the query string
    // "false" (a non-empty string) coerces to TRUE, and an explicit
    // ?includeInactive=false would wrongly reveal hidden schedules. Parse the
    // string form ourselves: only "true" (or a real boolean true) means true.
    includeInactive: z
        .preprocess((v) => (typeof v === 'string' ? v === 'true' : v), z.boolean())
        .default(false),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const ShiftScheduleListResponseSchema = z.object({
    schedules: z.array(ShiftScheduleResponseSchema),
    /** Count of schedules hidden by the default filter — CANCELLED **or** past
     *  their rangeEnd — so the UI can show "Show past & cancelled (N)" without
     *  fetching them. */
    inactiveCount: z.number().int(),
    /** Total schedules matching the current filter (active-only or all), for the
     *  pager — the list can grow past one page as a facility accumulates them. */
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/** A materialized occurrence (one row per shift-request) shown on the detail view. */
export const ShiftScheduleOccurrenceSchema = z.object({
    shiftRequestId: z.string().uuid(),
    scheduledStart: z.string(),
    scheduledEnd: z.string(),
    matchingState: MatchingStateSchema,
});
export const ShiftScheduleDetailResponseSchema = ShiftScheduleResponseSchema.extend({
    occurrences: z.array(ShiftScheduleOccurrenceSchema),
});
//# sourceMappingURL=schedule.schema.js.map